import { customElement, property } from 'lit/decorators.js';
import { html, LitElement, nothing } from 'lit';
import { ActionType, ButtonType } from './model';
import style from './ny-button.scss?inline';

@customElement('ny-button')
export class NyButton extends LitElement {
  @property({ type: Boolean, reflect: true }) active: boolean | undefined = undefined;
  @property({ type: Boolean, reflect: true }) disabled = false;
  @property() buttonType: ButtonType = 'standard-width-button';
  @property() actionType: ActionType = 'primary';
  @property() styleOverwrite: string | undefined = undefined;
  @property() type: 'button' | 'submit' | 'reset' = 'button';
  @property({ reflect: true }) theme: 'light' | 'dark' = 'light';
  @property({ type: Boolean, reflect: true }) isLoading = false;
  @property() href?: string;
  @property() target?: '_self' | '_blank' | '_parent' | '_top';
  @property() rel?: string;

  @property({ attribute: 'aria-label' }) ariaLabel: string | null = null;
  @property({ attribute: 'aria-labelledby' }) ariaLabelledBy: string | null = null;

  private selfControlled = true;

  protected firstUpdated() {
    this.selfControlled = this.active === undefined;
  }

  private get isToggleable() {
    return this.buttonType === 'outlined-toggle-button';
  }

  private get isLink() {
    return !!this.href;
  }

  private get computedRel() {
    if (this.rel) return this.rel;
    return this.target === '_blank' ? 'noopener noreferrer' : undefined;
  }

  private getButtonContent() {
    switch (this.buttonType) {
      case 'icon-button':
        return html`<slot name="svg"></slot>`;

      case 'standard-width-icon-button':
        return html`
          <span class="textAndIcon">
            <slot name="svg"></slot>
            <slot name="buttonLabel"></slot>
          </span>
        `;

      case 'two-line-high-width-button':
        return html`
          <span class="twoLineButton">
            <slot name="buttonLabel"></slot>
            <slot class="subText" name="buttonSubLabel"></slot>
          </span>
        `;

      default:
        return html`<slot name="buttonLabel"></slot>`;
    }
  }

  private handleClick(e: MouseEvent) {
    if (this.disabled || this.isLoading) {
      e.preventDefault();
      e.stopImmediatePropagation();
      return;
    }

    if (this.isLink) {
      return;
    }

    if (this.isToggleable && this.selfControlled) {
      this.active = !this.active;
    }

    if (this.type === 'submit') {
      this.closest('form')?.requestSubmit();
    }
  }

  private renderContent() {
    return html`
      <span class="button-content" aria-hidden=${this.isLoading ? 'true' : 'false'}>
        ${this.getButtonContent()}
      </span>

      ${this.isLoading
        ? html`
            <span class="loading-overlay" aria-hidden="true">
              <span class="loading-spinner"></span>
            </span>
          `
        : nothing}
    `;
  }

  render() {
    const toggleClass =
        !this.disabled && this.isToggleable
            ? this.active
                ? 'toggle-on'
                : 'toggle-off'
            : nothing;

    return html`
      ${this.isLink
        ? html`<a part="button" class=${toggleClass} style=${this.styleOverwrite ?? nothing}
                  href=${!this.disabled && !this.isLoading ? this.href : nothing} target=${this.target ?? nothing}
                  rel=${this.computedRel ?? nothing} actionType=${this.actionType} buttonType=${this.buttonType}
                  aria-label=${this.ariaLabel ?? nothing} aria-labelledby=${this.ariaLabelledBy ?? nothing}
                  aria-disabled=${this.disabled || this.isLoading ? 'true' : 'false'}
                  aria-busy=${this.isLoading ? 'true' : 'false'}
                  @click=${this.handleClick}>
                  ${this.renderContent()}
              </a>
              `
        : html`
                  <button part="button" class=${toggleClass} style=${this.styleOverwrite ?? nothing} type=${this.type}
                          actionType=${this.actionType} buttonType=${this.buttonType}
                          ?disabled=${this.disabled || this.isLoading} aria-label=${this.ariaLabel ?? nothing}
                          aria-labelledby=${this.ariaLabelledBy ?? nothing}
                          aria-pressed=${this.isToggleable ? String(!!this.active) : nothing}
                          aria-busy=${this.isLoading ? 'true' : 'false'} @click=${this.handleClick}>
                      ${this.renderContent()}
                  </button>
              `}
      <style>
        ${style}
      </style>
    `;
  }
}